import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ArrowLeft, Save, Upload, X, FileText, Eye, Download, Maximize2, Plus, Trash2, MapPin } from 'lucide-react';
import CaseLocationFields from '../components/CaseLocationFields';
import { PersonField } from '../components/PersonField';
import { NameHelperInput, isPersonNameField } from '../components/NameHelperInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Documents allowed to legally change ownership (mirrors backend OWNERSHIP_CHANGE_NAMES/CATEGORIES)
const OWNERSHIP_CHANGE_NAMES = new Set(['Mutation Register', 'Mutation Register Extract', 'RR Pakka Book']);
const OWNERSHIP_CHANGE_CATEGORIES = new Set(['Transaction Documents']);

const _SALUTATION_RE = /\b(mr|mrs|miss|ms|smt|sri|shri|sou|dr|prof|late|kum|kumari|m\/s|messrs)\.?\s+/gi;
const _RELATION_RE = /\b(s\/o|w\/o|d\/o|c\/o|son of|wife of|daughter of|child of|aged about\s+\d+\s+years?)\b[^,&]*/gi;
const _SPLIT_RE = /\s*(?:,|\band\b|&)\s*/i;

const normalizeOwnerSingle = (raw) => {
  if (!raw) return '';
  let s = String(raw);
  s = s.replace(_RELATION_RE, '');
  s = s.replace(_SALUTATION_RE, '');
  s = s.replace(/\([^)]*\)/g, '');
  s = s.replace(/[^\w\s]/g, ' ');
  s = s.replace(/\s+/g, ' ').trim().toLowerCase();
  return s;
};

const parseOwnerNames = (raw) => {
  if (!raw || !String(raw).trim()) return [];
  return String(raw)
    .split(_SPLIT_RE)
    .map((p) => normalizeOwnerSingle(p))
    .filter(Boolean);
};

const ownerSetsEqual = (a, b) => {
  if (!a.length || !b.length) return false;
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((x) => setB.has(x));
};

// Survey Block Component
const SurveyBlockEditor = ({ blocks, onChange, caseLocation }) => {
  const [locationData, setLocationData] = useState({});

  const handleBlockChange = (idx, field, value) => {
    const updated = [...blocks];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  };

  const addBlock = () => {
    // Pre-fill new block's location from case_location for consistency
    const seed = {};
    if (caseLocation) {
      if (caseLocation.district) seed.district = caseLocation.district;
      if (caseLocation.taluk) seed.taluk = caseLocation.taluk;
      if (caseLocation.hobli) seed.hobli = caseLocation.hobli;
      if (caseLocation.village) seed.village = caseLocation.village;
    }
    onChange([...blocks, seed]);
  };

  const removeBlock = (idx) => {
    if (blocks.length <= 1) return;
    onChange(blocks.filter((_, i) => i !== idx));
  };

  const fetchTaluks = async (idx, districtId) => {
    try {
      const res = await api.get(`/locations?level=taluk&parent_id=${districtId}`);
      setLocationData(prev => ({ ...prev, [`taluks_${idx}`]: res.data }));
    } catch (e) { /* ignore */ }
  };

  const fetchHoblis = async (idx, talukId) => {
    try {
      const res = await api.get(`/locations?level=hobli&parent_id=${talukId}`);
      setLocationData(prev => ({ ...prev, [`hoblis_${idx}`]: res.data }));
    } catch (e) { /* ignore */ }
  };

  const fetchVillages = async (idx, hobliId) => {
    try {
      const res = await api.get(`/locations?level=village&parent_id=${hobliId}`);
      setLocationData(prev => ({ ...prev, [`villages_${idx}`]: res.data }));
    } catch (e) { /* ignore */ }
  };

  // Fetch districts once
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await api.get('/locations?level=district');
        setLocationData(prev => ({ ...prev, districts: res.data }));
      } catch (e) { /* ignore */ }
    };
    fetchDistricts();
  }, []);

  return (
    <div className="space-y-4 md:col-span-2" data-testid="survey-block-editor">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold text-[#1e3a5f]">
          Survey Numbers <span className="text-red-500">*</span>
          <span className="ml-2 text-xs text-green-600 font-normal">(+ Add)</span>
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addBlock}
          className="text-green-600 border-green-600 hover:bg-green-50"
          data-testid="add-survey-block-btn"
        >
          <Plus size={14} className="mr-1" />
          Add Survey
        </Button>
      </div>

      {blocks.map((block, idx) => (
        <div key={idx} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 space-y-4" data-testid={`survey-block-${idx}`}>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-700">
              Survey #{idx + 1}
            </h4>
            {blocks.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeBlock(idx)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                data-testid={`remove-survey-${idx}`}
              >
                <Trash2 size={14} className="mr-1" />
                Remove
              </Button>
            )}
          </div>

          {/* Survey Numbers Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Sy. No.</Label>
              <Input
                value={block.sy_no || ''}
                onChange={(e) => handleBlockChange(idx, 'sy_no', e.target.value)}
                placeholder="e.g., 123/1"
                data-testid={`survey-sy-no-${idx}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Old Sy. No.</Label>
              <Input
                value={block.old_sy_no || ''}
                onChange={(e) => handleBlockChange(idx, 'old_sy_no', e.target.value)}
                placeholder="e.g., 123"
                data-testid={`survey-old-sy-no-${idx}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">New Sy. No.</Label>
              <Input
                value={block.new_sy_no || ''}
                onChange={(e) => handleBlockChange(idx, 'new_sy_no', e.target.value)}
                placeholder="e.g., 123/1A"
                data-testid={`survey-new-sy-no-${idx}`}
              />
            </div>
          </div>

          {/* Extent Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Extent (Acres)</Label>
              <Input
                type="number"
                value={block.acres || ''}
                onChange={(e) => handleBlockChange(idx, 'acres', e.target.value)}
                placeholder="Acres"
                data-testid={`survey-acres-${idx}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Extent (Guntas)</Label>
              <Input
                type="number"
                value={block.guntas || ''}
                onChange={(e) => handleBlockChange(idx, 'guntas', e.target.value)}
                placeholder="Guntas"
                data-testid={`survey-guntas-${idx}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs italic">Kharab (excluding)</Label>
              <Input
                value={block.kharab || ''}
                onChange={(e) => handleBlockChange(idx, 'kharab', e.target.value)}
                placeholder="e.g., 2 Guntas"
                data-testid={`survey-kharab-${idx}`}
              />
            </div>
          </div>

          {/* Location Row */}
          <div>
            <div className="flex items-center gap-1 mb-2">
              <MapPin size={14} className="text-slate-400" />
              <Label className="text-xs font-semibold text-slate-500">Location</Label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">District</Label>
                {block._manual_district ? (
                  <Input
                    value={block.district || ''}
                    onChange={(e) => handleBlockChange(idx, 'district', e.target.value)}
                    placeholder="Enter district"
                  />
                ) : (
                  <Select
                    value={block._district_id || ''}
                    onValueChange={(val) => {
                      const d = (locationData.districts || []).find(x => x.id === val);
                      const updated = { ...block, _district_id: val, district: d?.name || '', _taluk_id: '', taluk: '', _hobli_id: '', hobli: '', _village_id: '', village: '' };
                      const newBlocks = [...blocks];
                      newBlocks[idx] = updated;
                      onChange(newBlocks);
                      fetchTaluks(idx, val);
                    }}
                  >
                    <SelectTrigger className="h-9" data-testid={`survey-district-${idx}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {(locationData.districts || []).map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center gap-1 mt-1">
                  <Checkbox
                    id={`manual-district-${idx}`}
                    checked={block._manual_district || false}
                    onCheckedChange={(v) => handleBlockChange(idx, '_manual_district', v)}
                  />
                  <label htmlFor={`manual-district-${idx}`} className="text-[10px] text-slate-400">Manual</label>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Taluk</Label>
                {block._manual_taluk ? (
                  <Input
                    value={block.taluk || ''}
                    onChange={(e) => handleBlockChange(idx, 'taluk', e.target.value)}
                    placeholder="Enter taluk"
                  />
                ) : (
                  <Select
                    value={block._taluk_id || ''}
                    onValueChange={(val) => {
                      const t = (locationData[`taluks_${idx}`] || []).find(x => x.id === val);
                      const updated = { ...block, _taluk_id: val, taluk: t?.name || '', _hobli_id: '', hobli: '', _village_id: '', village: '' };
                      const newBlocks = [...blocks];
                      newBlocks[idx] = updated;
                      onChange(newBlocks);
                      fetchHoblis(idx, val);
                    }}
                    disabled={!block._district_id && !(locationData[`taluks_${idx}`] || []).length}
                  >
                    <SelectTrigger className="h-9" data-testid={`survey-taluk-${idx}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {(locationData[`taluks_${idx}`] || []).map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center gap-1 mt-1">
                  <Checkbox
                    id={`manual-taluk-${idx}`}
                    checked={block._manual_taluk || false}
                    onCheckedChange={(v) => handleBlockChange(idx, '_manual_taluk', v)}
                  />
                  <label htmlFor={`manual-taluk-${idx}`} className="text-[10px] text-slate-400">Manual</label>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hobli</Label>
                {block._manual_hobli ? (
                  <Input
                    value={block.hobli || ''}
                    onChange={(e) => handleBlockChange(idx, 'hobli', e.target.value)}
                    placeholder="Enter hobli"
                  />
                ) : (
                  <Select
                    value={block._hobli_id || ''}
                    onValueChange={(val) => {
                      const h = (locationData[`hoblis_${idx}`] || []).find(x => x.id === val);
                      const updated = { ...block, _hobli_id: val, hobli: h?.name || '', _village_id: '', village: '' };
                      const newBlocks = [...blocks];
                      newBlocks[idx] = updated;
                      onChange(newBlocks);
                      fetchVillages(idx, val);
                    }}
                    disabled={!block._taluk_id && !(locationData[`hoblis_${idx}`] || []).length}
                  >
                    <SelectTrigger className="h-9" data-testid={`survey-hobli-${idx}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {(locationData[`hoblis_${idx}`] || []).map(h => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center gap-1 mt-1">
                  <Checkbox
                    id={`manual-hobli-${idx}`}
                    checked={block._manual_hobli || false}
                    onCheckedChange={(v) => handleBlockChange(idx, '_manual_hobli', v)}
                  />
                  <label htmlFor={`manual-hobli-${idx}`} className="text-[10px] text-slate-400">Manual</label>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Village</Label>
                {block._manual_village ? (
                  <Input
                    value={block.village || ''}
                    onChange={(e) => handleBlockChange(idx, 'village', e.target.value)}
                    placeholder="Enter village"
                  />
                ) : (
                  <Select
                    value={block._village_id || ''}
                    onValueChange={(val) => {
                      const v = (locationData[`villages_${idx}`] || []).find(x => x.id === val);
                      handleBlockChange(idx, '_village_id', val);
                      handleBlockChange(idx, 'village', v?.name || '');
                    }}
                    disabled={!block._hobli_id && !(locationData[`villages_${idx}`] || []).length}
                  >
                    <SelectTrigger className="h-9" data-testid={`survey-village-${idx}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {(locationData[`villages_${idx}`] || []).map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center gap-1 mt-1">
                  <Checkbox
                    id={`manual-village-${idx}`}
                    checked={block._manual_village || false}
                    onCheckedChange={(v) => handleBlockChange(idx, '_manual_village', v)}
                  />
                  <label htmlFor={`manual-village-${idx}`} className="text-[10px] text-slate-400">Manual</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const DocumentEntryPage = () => {
  const { caseId, entryId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [documentType, setDocumentType] = useState(null);
  const [formData, setFormData] = useState({});
  const [remarks, setRemarks] = useState('');
  const [flowInclude, setFlowInclude] = useState(false);
  const [flowType, setFlowType] = useState('Supporting Document');
  const [flowBranchName, setFlowBranchName] = useState('');
  const [flowDisplayRemarks, setFlowDisplayRemarks] = useState(true);
  const [pendingDocuments, setPendingDocuments] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caseLocation, setCaseLocation] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Location-change confirmation state
  const [pendingSave, setPendingSave] = useState(null); // holds the cleaned form_data + location diff until user responds
  const [locationConfirmOpen, setLocationConfirmOpen] = useState(false);
  const [ownershipContext, setOwnershipContext] = useState(null); // { expected_normalized: [], expected_display: '', is_deed: bool, allows_change: bool }

  const isEditing = !!entryId;
  const docTypeId = searchParams.get('docType');

  const canEdit = (() => {
    if (!user || !caseData) return false;
    if (isAdmin) return true;
    if (caseData.created_by === user.id) return true;
    return (caseData.assigned_users || []).includes(user.id);
  })();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, docTypeId]);

  const fetchData = async () => {
    try {
      const [caseLocRes, caseRes, ownershipRes] = await Promise.all([
        api.get(`/cases/${caseId}/location`),
        api.get(`/cases/${caseId}`),
        api.get(`/cases/${caseId}/ownership-chain`).catch(() => ({ data: { chain: [], current_owners: [], current_owners_display: '' } })),
      ]);
      const caseLoc = caseLocRes.data;
      setCaseLocation(caseLoc);
      setCaseData(caseRes.data);
      const ownership = ownershipRes.data || { chain: [], current_owners: [], current_owners_display: '' };

      if (isEditing) {
        const entryRes = await api.get(`/document-entries/${entryId}`);
        const entry = entryRes.data;
        
        const docTypeRes = await api.get(`/document-types/${entry.document_type_id}`);
        setDocumentType(docTypeRes.data);
        // Ownership context for this entry — what owners were expected BEFORE this entry's position?
        const myChainItem = (ownership.chain || []).find((c) => c.entry_id === entryId);
        setOwnershipContext({
          expected_normalized: myChainItem ? myChainItem.expected_owners_before : ownership.current_owners,
          expected_display: myChainItem ? myChainItem.expected_owners_before_display : (ownership.current_owners_display || ''),
          is_deed: OWNERSHIP_CHANGE_CATEGORIES.has(docTypeRes.data.category || ''),
          allows_change:
            OWNERSHIP_CHANGE_CATEGORIES.has(docTypeRes.data.category || '') ||
            OWNERSHIP_CHANGE_NAMES.has(docTypeRes.data.name || ''),
        });

        // Backfill location fields (top-level and inside survey_blocks) from case_location
        // for display consistency with the rendered preview. Only fills EMPTY values;
        // stored data is untouched until user clicks Save.
        const formWithFallback = { ...(entry.form_data || {}) };
        if (caseLoc) {
          for (const k of ['district', 'taluk', 'hobli', 'village']) {
            if (!formWithFallback[k] && caseLoc[k]) {
              formWithFallback[k] = caseLoc[k];
            }
          }
          if (Array.isArray(formWithFallback.survey_blocks)) {
            formWithFallback.survey_blocks = formWithFallback.survey_blocks.map((b) => {
              const blk = { ...(b || {}) };
              for (const k of ['district', 'taluk', 'hobli', 'village']) {
                if (!blk[k] && caseLoc[k]) blk[k] = caseLoc[k];
              }
              return blk;
            });
          }
        }
        setFormData(formWithFallback);
        setRemarks(entry.remarks || '');
        setPendingDocuments(entry.pending_documents || '');
        if (entry.flow_include !== undefined && entry.flow_include !== null) setFlowInclude(!!entry.flow_include);
        if (entry.flow_type) setFlowType(entry.flow_type);
        setFlowBranchName(entry.flow_branch_name || '');
        setFlowDisplayRemarks(entry.flow_display_remarks !== false);
        setUploadedFiles(entry.uploaded_files || []);
      } else if (docTypeId) {
        const docTypeRes = await api.get(`/document-types/${docTypeId}`);
        setDocumentType(docTypeRes.data);
        // Ownership context for new entry — expected = current chain state (appended at end)
        setOwnershipContext({
          expected_normalized: ownership.current_owners || [],
          expected_display: ownership.current_owners_display || '',
          is_deed: OWNERSHIP_CHANGE_CATEGORIES.has(docTypeRes.data.category || ''),
          allows_change:
            OWNERSHIP_CHANGE_CATEGORIES.has(docTypeRes.data.category || '') ||
            OWNERSHIP_CHANGE_NAMES.has(docTypeRes.data.name || ''),
        });
        
        const initialData = {};
        docTypeRes.data.fields.forEach(field => {
          if (field.type === 'survey_block') {
            // Pre-fill the first block's location fields from case_location
            // so deed forms show the location (and persist it when saved).
            const firstBlock = {};
            if (caseLoc) {
              if (caseLoc.district) firstBlock.district = caseLoc.district;
              if (caseLoc.taluk) firstBlock.taluk = caseLoc.taluk;
              if (caseLoc.hobli) firstBlock.hobli = caseLoc.hobli;
              if (caseLoc.village) firstBlock.village = caseLoc.village;
            }
            initialData[field.name] = [firstBlock];
          } else if (field.repeatable) {
            initialData[field.name] = [''];
          } else if (field.type === 'location' && caseLoc && caseLoc[field.name]) {
            // Auto-fill location from case
            initialData[field.name] = caseLoc[field.name];
          } else {
            initialData[field.name] = field.default || '';
          }
        });
        setFormData(initialData);
      }
    } catch (error) {
      toast.error('Failed to load data');
      navigate(`/cases/${caseId}`);
    } finally {
      setLoading(false);
    }
  };

  // Clean survey blocks before saving (remove internal state keys)
  const cleanFormDataForSave = (data) => {
    const cleaned = { ...data };
    if (cleaned.survey_blocks && Array.isArray(cleaned.survey_blocks)) {
      cleaned.survey_blocks = cleaned.survey_blocks.map(block => {
        const clean = {};
        for (const [key, value] of Object.entries(block)) {
          if (!key.startsWith('_')) {
            clean[key] = value;
          }
        }
        return clean;
      });
    }
    return cleaned;
  };

  // Persist the entry (+ optionally propagate location to the case)
  const persistEntry = async (cleanedData, propagateLocation) => {
    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/document-entries/${entryId}`, {
          form_data: cleanedData,
          remarks: remarks || null,
          pending_documents: pendingDocuments || null,
          flow_include: flowInclude,
          flow_type: flowType,
          flow_branch_name: flowType === 'Branch Start' ? (flowBranchName || null) : null,
          flow_display_remarks: flowDisplayRemarks,
        });
        toast.success('Entry updated successfully');
      } else {
        await api.post('/document-entries', {
          case_id: caseId,
          document_type_id: docTypeId,
          form_data: cleanedData,
          remarks: remarks || null,
          pending_documents: pendingDocuments || null,
          flow_include: flowInclude,
          flow_type: flowType,
          flow_branch_name: flowType === 'Branch Start' ? (flowBranchName || null) : null,
          flow_display_remarks: flowDisplayRemarks,
        });
        toast.success('Entry created successfully');
      }

      if (propagateLocation) {
        const locUpdate = {};
        if (cleanedData.district) locUpdate.district = cleanedData.district;
        if (cleanedData.taluk) locUpdate.taluk = cleanedData.taluk;
        if (cleanedData.hobli) locUpdate.hobli = cleanedData.hobli;
        if (cleanedData.village) locUpdate.village = cleanedData.village;
        if (Object.keys(locUpdate).length > 0) {
          try {
            await api.put(`/cases/${caseId}/location`, locUpdate);
            toast.success('Case location updated');
          } catch (err) {
            /* non-fatal */
          }
        }
      }

      navigate(`/cases/${caseId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  // Returns true if any top-level location field in `data` differs from the baseline caseLocation
  const hasLocationChanged = (data) => {
    const base = caseLocation || {};
    const levels = ['district', 'taluk', 'hobli', 'village'];
    return levels.some((k) => {
      const formVal = (data[k] || '').trim();
      const baseVal = (base[k] || '').trim();
      // Consider it a change only if both sides have a value and they differ
      // (ignores the case where case had no location and user filled it — we still want to prompt to propagate)
      if (!formVal) return false;
      return formVal !== baseVal;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanedData = cleanFormDataForSave(formData);

    if (!hasLocationFields) {
      // No location fields on this doc — just save
      await persistEntry(cleanedData, false);
      return;
    }

    if (hasLocationChanged(cleanedData)) {
      // Ask before propagating
      setPendingSave(cleanedData);
      setLocationConfirmOpen(true);
      return;
    }

    // No change — save entry without touching case
    await persistEntry(cleanedData, false);
  };

  const handleFileUpload = async (e) => {
    if (!isEditing) {
      toast.error('Please save the entry first before uploading files');
      return;
    }
    const file = e.target.files[0];
    if (!file) return;
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    setUploading(true);
    try {
      const res = await api.post(`/document-entries/${entryId}/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFiles(prev => [...prev, res.data.filename]);
      toast.success('File uploaded');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (filename) => {
    try {
      await api.delete(`/document-entries/${entryId}/files/${filename}`);
      setUploadedFiles(prev => prev.filter(f => f !== filename));
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleRepeatableFieldChange = (fieldName, index, newValue) => {
    setFormData(prev => {
      const currentValues = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [''];
      currentValues[index] = newValue;
      return { ...prev, [fieldName]: currentValues };
    });
  };

  const addRepeatableEntry = (fieldName) => {
    setFormData(prev => {
      const currentValues = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [''];
      return { ...prev, [fieldName]: [...currentValues, ''] };
    });
  };

  const removeRepeatableEntry = (fieldName, index) => {
    setFormData(prev => {
      const currentValues = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [''];
      if (currentValues.length <= 1) return prev;
      return { ...prev, [fieldName]: currentValues.filter((_, i) => i !== index) };
    });
  };

  const renderField = (field) => {
    if (field.type === 'location') return null;

    // Person / Person-with-relation widgets
    if (field.type === 'person' || field.type === 'person_with_relation') {
      return (
        <div key={field.name} className="space-y-2">
          <Label>
            {field.label || field.name}
            {field.required && <span className="text-red-500 ml-1">*</span>}
            {field.bold && (
              <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                Bold in output
              </span>
            )}
          </Label>
          <PersonField
            value={formData[field.name] || ''}
            withRelation={field.type === 'person_with_relation'}
            onChange={(v) => setFormData((prev) => ({ ...prev, [field.name]: v }))}
            testId={`field-${field.name}`}
          />
        </div>
      );
    }

    // Survey block renderer
    if (field.type === 'survey_block') {
      const blocks = Array.isArray(formData[field.name]) ? formData[field.name] : [{}];
      return (
        <SurveyBlockEditor
          key={field.name}
          blocks={blocks}
          caseLocation={caseLocation}
          onChange={(newBlocks) => setFormData(prev => ({ ...prev, [field.name]: newBlocks }))}
        />
      );
    }

    if (field.repeatable) {
      const values = Array.isArray(formData[field.name]) ? formData[field.name] : [formData[field.name] || ''];
      return (
        <div key={field.name} className="space-y-2 md:col-span-2">
          <div className="flex items-center justify-between">
            <Label>
              {field.label} {field.required && <span className="text-red-500">*</span>}
              <span className="ml-2 text-xs text-green-600 font-normal">(+ Add)</span>
            </Label>
            <Button type="button" variant="outline" size="sm" onClick={() => addRepeatableEntry(field.name)} className="text-green-600 border-green-600 hover:bg-green-50">
              + Add
            </Button>
          </div>
          <div className="space-y-2">
            {values.map((value, index) => (
              <div key={index} className="flex gap-2 items-start">
                {isPersonNameField(field) ? (
                  <div className="flex-1">
                    <NameHelperInput
                      value={value}
                      onChange={(v) => handleRepeatableFieldChange(field.name, index, v)}
                      placeholder={`Enter ${field.label} ${index + 1}`}
                      testId={`field-${field.name}-${index}`}
                    />
                  </div>
                ) : (
                  <Input
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={value}
                    onChange={(e) => handleRepeatableFieldChange(field.name, index, e.target.value)}
                    placeholder={`Enter ${field.label} ${index + 1}`}
                    data-testid={`field-${field.name}-${index}`}
                    className="flex-1"
                  />
                )}
                {values.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeRepeatableEntry(field.name, index)} className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-1">
                    <X size={16} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    const value = formData[field.name] || '';

    if (field.type === 'select' && field.options) {
      return (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={field.name}>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
          <Select value={value} onValueChange={(val) => setFormData(prev => ({ ...prev, [field.name]: val }))}>
            <SelectTrigger data-testid={`field-${field.name}`}>
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.name} className="space-y-2 md:col-span-2">
          <Label htmlFor={field.name}>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
          <Textarea id={field.name} value={value} onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))} placeholder={`Enter ${field.label}`} rows={3} data-testid={`field-${field.name}`} />
        </div>
      );
    }

    if (field.type === 'date') {
      return (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={field.name}>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
          <Input id={field.name} type="date" value={value} onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))} data-testid={`field-${field.name}`} />
        </div>
      );
    }

    if (field.type === 'number') {
      return (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={field.name}>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
          <Input id={field.name} type="number" value={value} onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))} placeholder={`Enter ${field.label}`} data-testid={`field-${field.name}`} />
        </div>
      );
    }

    if (isPersonNameField(field)) {
      return (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={field.name}>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
          <NameHelperInput
            id={field.name}
            value={value}
            onChange={(v) => setFormData((prev) => ({ ...prev, [field.name]: v }))}
            placeholder={`Enter ${field.label}`}
            testId={`field-${field.name}`}
          />
        </div>
      );
    }

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <Input id={field.name} type="text" value={value} onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))} placeholder={`Enter ${field.label}`} data-testid={`field-${field.name}`} />
      </div>
    );
  };

  const hasLocationFields = documentType?.fields?.some(f => f.type === 'location');
  const hasSurveyBlock = documentType?.fields?.some(f => f.type === 'survey_block');
  const isImageFile = (filename) => /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
  const isPdfFile = (filename) => /\.pdf$/i.test(filename);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="document-entry-page">
      <div className="flex items-center gap-4">
        <Link to={`/cases/${caseId}`}>
          <Button variant="ghost" size="icon">
            <ArrowLeft size={20} />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
            {isEditing ? 'Edit' : 'Add'} Document Entry
          </h1>
          <p className="text-slate-500">{documentType?.name}</p>
        </div>
      </div>

      {(() => {
        if (!ownershipContext || !documentType) return null;
        const expectedNorm = ownershipContext.expected_normalized || [];
        // If no chain established yet, nothing to validate against
        if (expectedNorm.length === 0) {
          return (
            <div
              className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
              data-testid="ownership-inline-info"
            >
              <span className="font-medium text-slate-700">Ownership chain:</span>{' '}
              This is the first ownership-bearing document. The owner entered here will
              establish the chain for following documents.
            </div>
          );
        }

        let warning = false;
        let warningMsg = '';
        let currentRaw = '';
        let currentLabel = '';

        if (ownershipContext.is_deed) {
          // Validate executed_by
          const ebv = formData.executed_by;
          currentRaw = Array.isArray(ebv) ? ebv.filter(Boolean).join(', ') : (ebv || '');
          currentLabel = 'Executed By';
          const enteredNorm = parseOwnerNames(currentRaw);
          if (enteredNorm.length > 0 && !ownerSetsEqual(enteredNorm, expectedNorm)) {
            warning = true;
            warningMsg =
              '"Executed By" does not match the current ownership chain. Expected previous owner(s): ';
          }
        } else if (!ownershipContext.allows_change) {
          // Non ownership-change doc: owner_name must match
          const onv = formData.owner_name ?? formData.owners;
          currentRaw = Array.isArray(onv) ? onv.filter(Boolean).join(', ') : (onv || '');
          currentLabel = 'Owner Name';
          const enteredNorm = parseOwnerNames(currentRaw);
          if (enteredNorm.length > 0 && !ownerSetsEqual(enteredNorm, expectedNorm)) {
            warning = true;
            warningMsg =
              'Owner name differs from ownership continuity. Ownership change is allowed only through Deeds, Mutation Register, or RR Pakka Book. Expected: ';
          }
        }
        // Mutation/RR Pakka: ownership change is allowed → no warning needed

        if (warning) {
          return (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2"
              data-testid="ownership-inline-warning"
            >
              <span className="text-amber-700 mt-0.5">⚠</span>
              <div className="flex-1 text-xs text-amber-800">
                <p className="font-medium">{warningMsg}<span className="font-semibold">"{ownershipContext.expected_display}"</span></p>
                <p className="mt-1">
                  {currentLabel}: <span className="font-medium">{currentRaw || '—'}</span>. The
                  document will still save, but please re-check for typos or missing intermediate Deed/Mutation/RR Pakka entries.
                </p>
              </div>
            </div>
          );
        }
        return (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
            data-testid="ownership-inline-info"
          >
            <span className="font-medium text-slate-700">Expected owner(s) per chain:</span>{' '}
            {ownershipContext.expected_display || expectedNorm.join(', ')}
            {ownershipContext.allows_change && !ownershipContext.is_deed && (
              <span className="text-slate-500"> · This document type may legally change ownership.</span>
            )}
            {ownershipContext.is_deed && (
              <span className="text-slate-500"> · Deed: Executed By must match the previous owner; In Favour Of becomes the new owner.</span>
            )}
          </div>
        );
      })()}

      <form onSubmit={handleSubmit}>
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f]">Document Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {documentType?.fields.filter(f => f.type !== 'location').map(renderField)}
            </div>
          </CardContent>
        </Card>

        {/* Location Section - only for non-survey-block types */}
        {hasLocationFields && !hasSurveyBlock && (
          <Card className="border-0 shadow-sm mt-6">
            <CardHeader>
              <CardTitle className="text-lg text-[#1e3a5f]">Location Details (Karnataka)</CardTitle>
              <p className="text-sm text-slate-500">
                {caseLocation && (caseLocation.district || caseLocation.taluk || caseLocation.hobli || caseLocation.village) ? (
                  <span data-testid="location-autofill-hint">
                    <MapPin size={12} className="inline -mt-0.5 mr-1 text-green-600" />
                    <span className="text-green-700">Auto-filled from case.</span> Edit below if this document refers to a different location.
                  </span>
                ) : (
                  'Select from dropdown or enable manual entry'
                )}
              </p>
            </CardHeader>
            <CardContent>
              <CaseLocationFields
                showHeader={false}
                testIdPrefix="field"
                value={{
                  district: formData.district || '',
                  taluk: formData.taluk || '',
                  hobli: formData.hobli || '',
                  village: formData.village || '',
                }}
                onChange={(loc) => setFormData(prev => ({
                  ...prev,
                  district: loc.district || '',
                  taluk: loc.taluk || '',
                  hobli: loc.hobli || '',
                  village: loc.village || '',
                }))}
              />
            </CardContent>
          </Card>
        )}

        {/* Remarks */}
        <Card className="border-0 shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f]">Remarks (Optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Add any remarks or notes..." rows={3} data-testid="remarks-input" />
            <p className="text-xs text-slate-400 mt-2">Remarks will appear below the document paragraph in the DDR.</p>
          </CardContent>
        </Card>

        {/* Pending Documents (per-entry; aggregated into the Pending Documents export, NOT in the DDR) */}
        <Card className="border-0 shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f]">Pending Documents (Optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={pendingDocuments}
              onChange={(e) => setPendingDocuments(e.target.value)}
              placeholder={"One per line, e.g.:\nOriginal Sale Deed pending\nLatest RTC required"}
              rows={3}
              data-testid="pending-documents-input"
            />
            <p className="text-xs text-slate-400 mt-2">
              Listed in the case-level <b>Pending Documents</b> export only. Never appears inside the DDR.
            </p>
          </CardContent>
        </Card>

        {/* File Uploads */}
        <Card className="border-0 shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f]">Upload Documents</CardTitle>
            <p className="text-sm text-slate-500">Files are for verification only, not included in DDR</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {uploadedFiles.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {uploadedFiles.map((filename, fileIdx) => (
                    <div key={filename} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {isImageFile(filename) ? (
                          <div className="w-12 h-12 rounded bg-slate-200 overflow-hidden flex-shrink-0">
                            <img src={`${BACKEND_URL}/api/uploads/${filename}`} alt={filename} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded bg-red-100 flex items-center justify-center flex-shrink-0">
                            <FileText size={24} className="text-red-500" />
                          </div>
                        )}
                        <span className="text-sm text-slate-700 truncate" data-testid={`file-name-${fileIdx}`}>
                          {documentType?.name || 'Document'}{uploadedFiles.length > 1 ? ` (${fileIdx + 1})` : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="ghost" size="icon" onClick={() => { setPreviewFile(filename); setPreviewOpen(true); }} className="text-blue-500 hover:text-blue-700">
                          <Eye size={18} />
                        </Button>
                        <a href={`${BACKEND_URL}/api/uploads/${filename}`} target="_blank" rel="noopener noreferrer" download>
                          <Button type="button" variant="ghost" size="icon" className="text-green-500 hover:text-green-700"><Download size={18} /></Button>
                        </a>
                        <Button type="button" variant="ghost" size="icon" onClick={() => handleDeleteFile(filename)} className="text-red-500 hover:text-red-700"><X size={18} /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                <input type="file" id="file-upload" className="hidden" onChange={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png,.gif" />
                <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                  <Upload size={32} className="text-slate-400 mb-2" />
                  <span className="text-sm text-slate-500">{uploading ? 'Uploading...' : 'Click to upload PDF or Image'}</span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end items-center gap-4 mt-6">
          {!canEdit && caseData && (
            <span
              className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-600 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full mr-auto"
              data-testid="entry-reference-only"
            >
              Reference Only — Cannot edit
            </span>
          )}
          <Link to={`/cases/${caseId}`}>
            <Button type="button" variant="outline">{canEdit ? 'Cancel' : 'Back'}</Button>
          </Link>
          {canEdit && (
            <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#162c46]" disabled={saving} data-testid="save-entry-btn">
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save size={18} />
                  {isEditing ? 'Update Entry' : 'Save Entry'}
                </span>
              )}
            </Button>
          )}
        </div>
      </form>

      {/* Location-change confirmation dialog */}
      <Dialog open={locationConfirmOpen} onOpenChange={setLocationConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="location-propagate-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Update location for the entire case?
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 text-sm text-slate-600 space-y-3">
            <p>You changed the location on this document from the case-level default.</p>
            <div className="rounded-lg border border-slate-200 overflow-hidden text-xs">
              <div className="grid grid-cols-2 bg-slate-50 px-3 py-2 font-semibold text-slate-500">
                <span>Case default</span>
                <span>This document</span>
              </div>
              {['district', 'taluk', 'hobli', 'village'].map((k) => {
                const base = (caseLocation && caseLocation[k]) || '—';
                const nowv = (pendingSave && pendingSave[k]) || '—';
                if (base === nowv) return null;
                return (
                  <div key={k} className="grid grid-cols-2 px-3 py-2 border-t border-slate-100">
                    <span className="text-slate-500 capitalize">{k}: <span className="text-slate-700">{base}</span></span>
                    <span className="capitalize text-[#1e3a5f] font-medium">{k}: {nowv}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500">
              Yes — apply this location to the entire case (all future documents auto-fill with these values).<br />
              No — save only on this document; leave the case-level location unchanged.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => { setLocationConfirmOpen(false); setPendingSave(null); }}
              data-testid="location-propagate-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                const data = pendingSave;
                setLocationConfirmOpen(false);
                setPendingSave(null);
                if (data) await persistEntry(data, false);
              }}
              data-testid="location-propagate-no"
            >
              No, only this document
            </Button>
            <Button
              className="bg-[#1e3a5f] hover:bg-[#162c46]"
              onClick={async () => {
                const data = pendingSave;
                setLocationConfirmOpen(false);
                setPendingSave(null);
                if (data) await persistEntry(data, true);
              }}
              data-testid="location-propagate-yes"
            >
              Yes, update case
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>File Preview: {previewFile}</span>
              <a href={`${BACKEND_URL}/api/uploads/${previewFile}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm"><Maximize2 size={16} className="mr-2" />Open in New Tab</Button>
              </a>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh]">
            {previewFile && isImageFile(previewFile) ? (
              <img src={`${BACKEND_URL}/api/uploads/${previewFile}`} alt={previewFile} className="max-w-full h-auto" />
            ) : previewFile && isPdfFile(previewFile) ? (
              <iframe src={`${BACKEND_URL}/api/uploads/${previewFile}`} className="w-full h-[70vh]" title={previewFile} />
            ) : (
              <div className="text-center py-12">
                <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Preview not available.</p>
                <a href={`${BACKEND_URL}/api/uploads/${previewFile}`} target="_blank" rel="noopener noreferrer" download>
                  <Button className="mt-4"><Download size={18} className="mr-2" />Download File</Button>
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentEntryPage;
